import { useCallback, useState, useEffect } from "react";
import "./css/admin.css";
import axiosConfig from "../config.js";
import { formatDateTime } from "../util.js";
import { CheckIcon, XIcon } from "lucide-react";
import Pagination from "../components/dashboard/Pagination.jsx";
import { SortAscIcon, SortDescIcon, CircleHelpIcon } from "lucide-react";
import BooleanDisplay, {True, False} from "../components/misc/BooleanDisplay.jsx";


import Bowser from "bowser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faApple, faMicrosoft , faUbuntu, faAndroid, faLinux } from "@fortawesome/free-brands-svg-icons";

export default function AdminAuthentication() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for pagination 
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);

      const res = await axiosConfig.get("/logs/auth", {
        params: {
          page: page,
          pageSize: pageSize,
          sortBy: sortBy,
          sortOrder: sortOrder,
        }
      });

      const data = res.data;
      setEvents(data.events);
      setTotalPages(data.totalPages);

    } catch (err) {
      console.error(err);
      setError("Failed to fetch authentication logs");
    } finally {
      setLoading(false);
    }
  }, [pageSize, page, sortBy, sortOrder]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs])

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const onSort = (key) => {
    if (sortBy === key) {
      // flip
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // start sort
      setSortBy(key);
      setSortOrder("asc");
    }
    setPage(1);
  }

    const UserAgentDisplay = ({ userAgent }) => {
      const parser = Bowser.getParser(userAgent);
  
      let osIcon = <CircleHelpIcon size={16} />;
  
      let os = parser.getOSName();
  
      if (os === "macOS" || os.is ==="iOS" || os.is === "iPadOS") {
        osIcon = <FontAwesomeIcon icon={faApple} />
      } else if (os === "Windows") {
        osIcon = <FontAwesomeIcon icon={faMicrosoft} />
      } else if (os === "Android") {
        osIcon = <FontAwesomeIcon icon={faAndroid} />
      } else if (os === "Ubuntu") {
        osIcon = <FontAwesomeIcon icon={faUbuntu} />
      } else if (userAgent.includes("Linux")) {
        osIcon = <FontAwesomeIcon icon={faLinux} />
      }
      
  
      return (
        <span 
          title={userAgent.length > 24 ? userAgent : null} 
          className={userAgent.length > 24 ? "truncated-text" : ""}
          style={{display: "block"}}
        >
          {osIcon}
          <span style={{marginLeft: ".5em"}}>{userAgent.length > 24 ? userAgent.substring(0, 24) + "..." : userAgent}</span>
        </span>
      )
    }

  const columns = [
    {key: "date", header: "DateTime"},
    {key: "userId", header: "User ID"},
    {key: "actionType", header: "Auth Action"},
    {key: "success", header: "Success"},
    {key: "ipAddress", header: "IP"},
    {key: "userAgent", header: "User Agent"},
    {key: "status", header: "Message"},
  ]

  return (
    <div className="dashboard-container">
      <h2> Authentication Logs </h2>
      <div className="admin-header">
        <div className="admin-title">
          <p> Monitor login attempts and authentication-related activities </p>
        </div>
      </div>
      <div className="table-section">
        <table className="admin-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th 
                  key={col.key}
                  onClick={() => onSort(col.key)}
                >
                  <div className="table-header-content">
                    {col.header}
                    {sortBy === col.key && (
                      <span> {sortOrder === "asc" ? <SortAscIcon size={16} /> : <SortDescIcon size={16} />} </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} align="center">Loading authentication events...</td></tr>
              ) : error ? (
                <tr><td colSpan={6} align="center">Error while loading events: {error}</td></tr>
              ) : events && events.length > 0 ? (
                events.map((evt) => (
                  <tr key={evt._id} className={evt.success === true ? "" : "error-row"}>
                    <td>{formatDateTime(evt.date)}</td>
                    <td>{evt.userId !== -1 ? evt.userId : "\u2013"}</td>
                    <td>{evt.actionType}</td>
                    <td><BooleanDisplay value={evt.success} /></td>
                    <td>{evt.ipAddress ? evt.ipAddress : "N/A"}</td>
                    <td>
                      <UserAgentDisplay userAgent={evt.userAgent} />
                    </td>
                    <td>{evt.status}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={6} align="center"> No authentication events found </td></tr>
              )}
          </tbody>
        </table>
        <Pagination page={page} 
          pageSize={pageSize} totalPages={totalPages} 
          onPageChange={handlePageChange} onPageSizeChange={setPageSize}
        />
      </div>
    </div>
  ); 
}